import * as React from "react";
import { motion } from "motion/react";
import { Slot } from "@radix-ui/react-slot";

import { cn } from "../lib/cn";
import { badgeVariants } from "../primitives/Badge";
import { PulseDot } from "./PulseDot";
import { getExperienceStateMeta, type ExperienceStateTone } from "./ExperienceStateBadge";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { transitions } from "../lib/motion";

/**
 * StatusExpression — the lifecycle-status vocabulary for the Brain.
 *
 * An artifact's status *converges over time*: an experience goes
 * `generating → ready → published`; a file goes `indexing → indexed`;
 * a connector goes `syncing → synced`; anything can land in `failed`.
 * The Brain design brief (unified#103) requires this be **encoded in form
 * (chip, stripe, progress) not just text**, and that the publish moment
 * **flips the same card in place** (no duplicate card, scroll/focus kept).
 *
 * This module ships the coordinated, presentational affordances every
 * Brain surface (the artifact tile, the AIKO side-preview, Studios cards)
 * mounts so status rendering is defined once:
 *   - `<StatusChip>`      — leading dot + label, color/fill by token.
 *   - `statusRingClass()` — a full-perimeter status keyline + faint tint for a
 *                           whole card (the recommended card-level signal).
 *   - `<StatusStripe>`    — a thin accent stripe primitive (kept for any
 *                           consumer that wants a small peripheral edge mark;
 *                           NOT the recommended whole-card treatment — a
 *                           one-sided edge reads as an unintentional accent).
 *   - `<StatusProgress>`  — a thin progress line, only while generating.
 *
 * Single source of truth: labels + tones route through the library's existing
 * `getExperienceStateMeta` (extended here with `generating`/`ready`/`failed`)
 * rather than a second lifecycle table; `STATUS_ALIAS` collapses each surface's
 * domain word (Indexing / Syncing / Live / Error / ...) onto the five canonical
 * states before the lookup, so connectors, files, and experiences share one
 * vocabulary (Adoption Handoff L216).
 *
 * Token-driven, zero raw hex. Theme-aware (light + `.dark` via semantic
 * tokens). Reduced-motion safe: status meaning is carried by the word + dot
 * color, never gated behind animation.
 *
 * @example
 *   <StatusChip status="generating" />            // shimmer chip + ShinyText
 *   <StatusChip status="Indexed" />               // alias → ready, settled dot
 *   <div className={cn("rounded-xl border bg-card p-4", statusRingClass(item.status))} />
 *   <StatusProgress status="generating" value={42} valueText="Indexing 3 of 7 pages" />
 */

/* ============================================================================
 * Canonical status model + alias map
 * ========================================================================= */

/** The five canonical lifecycle states (unified#103 `/items/` `status`). */
export type StatusExpressionState = "generating" | "ready" | "draft" | "published" | "failed";

/**
 * Domain-word → canonical-state map. Callers pass their surface's own status
 * literal (a file's `"Indexing"`, a connector's `"Synced"`, an experience's
 * `"published"`) and get the canonical treatment. Keys are lowercased before
 * lookup, so casing does not matter. Unknown words fall back to `draft`
 * (the neutral "we don't have a settled signal yet" read).
 */
export const STATUS_ALIAS: Record<string, StatusExpressionState> = {
  // in-flight / converging
  generating: "generating",
  in_progress: "generating",
  inprogress: "generating",
  processing: "generating",
  indexing: "generating",
  syncing: "generating",
  connecting: "generating",
  // settled / healthy (not yet public)
  ready: "ready",
  ready_to_publish: "ready",
  indexed: "ready",
  synced: "ready",
  connected: "ready",
  // unpublished work-in-hand
  draft: "draft",
  // public
  published: "published",
  live: "published",
  active: "published",
  // terminal failure
  failed: "failed",
  error: "failed",
  violation: "failed",
  disconnected: "failed",
};

/** Resolve any domain status word to one of the five canonical states. */
export const resolveStatus = (status: string | null | undefined): StatusExpressionState =>
  STATUS_ALIAS[(status ?? "").toLowerCase().trim()] ?? "draft";

/* ============================================================================
 * statusToken — the single status → presentation mapping
 * ========================================================================= */

type BadgeVariant = "info" | "success" | "private" | "published" | "destructive";
type DotKind = "none" | "settled" | "muted" | "live" | "error";

export interface StatusTokenInfo {
  /** The canonical state the input resolved to. */
  status: StatusExpressionState;
  /** Canonical human label (from `getExperienceStateMeta`). */
  label: string;
  /** Canonical tone (from `getExperienceStateMeta`). */
  tone: ExperienceStateTone;
  /** The semantic CSS color token this state paints with (dot + stripe + progress). */
  token: "--info" | "--success" | "--muted-foreground" | "--destructive";
  /** The `Badge` variant whose chrome matches this state. */
  badgeVariant: BadgeVariant;
  /** Which leading indicator the chip renders. */
  dot: DotKind;
  /** Whether the chip animates an in-progress shimmer + ShinyText label. */
  inProgress: boolean;
}

const STATUS_PRESENTATION: Record<
  StatusExpressionState,
  { token: StatusTokenInfo["token"]; badgeVariant: BadgeVariant; dot: DotKind; inProgress: boolean }
> = {
  // NOTE(palette): the in-progress accent is mapped to the library-canonical
  // `info` tone (matching `ExperienceStateBadge`'s existing `in_progress: info`
  // and aisquare-ui#124 §6). The #125 mock draws it on `--accent`; deferring to
  // lib consistency keeps one source of truth. Flagged for owner reconciliation.
  generating: { token: "--info", badgeVariant: "info", dot: "none", inProgress: true },
  ready: { token: "--success", badgeVariant: "success", dot: "settled", inProgress: false },
  draft: { token: "--muted-foreground", badgeVariant: "private", dot: "muted", inProgress: false },
  published: { token: "--success", badgeVariant: "published", dot: "live", inProgress: false },
  failed: { token: "--destructive", badgeVariant: "destructive", dot: "error", inProgress: false },
};

/**
 * The canonical status → presentation mapping: label + tone (from the shared
 * `getExperienceStateMeta`), the color token, the matching `Badge` variant, and
 * the indicator kind. Exported so non-chip surfaces (filters, list meta, toasts)
 * reuse the exact same resolution.
 */
export const statusToken = (status: string | null | undefined): StatusTokenInfo => {
  const canonical = resolveStatus(status);
  const meta = getExperienceStateMeta(canonical);
  const present = STATUS_PRESENTATION[canonical];
  return { status: canonical, label: meta.label, tone: meta.tone, ...present };
};

/* ============================================================================
 * statusRingClass — full-perimeter card status chrome
 * ========================================================================= */

/**
 * Per-state, full-perimeter card chrome: a complete token-toned keyline plus a
 * faint matching tint, keyed by canonical state. This is the recommended way to
 * carry an artifact's status on a *whole card* — it traces the entire silhouette
 * rather than painting a single edge, so it reads as an intentional status
 * treatment instead of a stray one-sided accent.
 *
 * Mirrors `STRIPE_TOKEN_CLASS` 1:1 (same token per state) so the card border and
 * the chip/dot stay one source of truth. Uses the same `border-{token}/30` +
 * `bg-{token}/[…]` soft triad as `Badge`'s semantic variants — zero raw hex,
 * theme-aware via semantic tokens. `draft` stays deliberately quiet (neutral
 * `border-border`, no tint) so unpublished work doesn't shout.
 */
const STATUS_RING_CLASS: Record<StatusExpressionState, string> = {
  generating: "border-info/30 bg-info/[0.04]",
  ready: "border-success/30 bg-success/[0.04]",
  draft: "border-border",
  published: "border-success/30 bg-success/[0.04]",
  failed: "border-destructive/30 bg-destructive/[0.05]",
};

/**
 * Resolve any domain status word to the full-perimeter card chrome (a complete
 * `border` keyline + faint tint). Drop it onto a card root alongside a plain
 * `border` utility, e.g.
 *
 *   <div className={cn("rounded-xl border bg-card p-4 transition-colors", statusRingClass(item.status))}>
 *
 * Pair it with `transition-colors` so a flip-in-place publish re-tones the whole
 * border in place (the same guarantee `StatusChip` gives), and it never paints a
 * single-side accent.
 */
export const statusRingClass = (status: string | null | undefined): string =>
  STATUS_RING_CLASS[resolveStatus(status)];

/* ============================================================================
 * Dot — token-colored leading indicator
 * ========================================================================= */

const DOT_TOKEN_CLASS: Record<Exclude<DotKind, "none" | "live">, string> = {
  settled: "bg-success",
  muted: "bg-muted-foreground",
  error: "bg-destructive",
};

function StatusDot({ kind }: { kind: DotKind }) {
  if (kind === "none") return null;
  // The published "live" dot pulses; everything settled is a static dot. The
  // dot is decorative (the label carries the accessible status) → aria-hidden.
  if (kind === "live") {
    return <PulseDot tone="success" size="sm" aria-hidden className="mr-0.5" />;
  }
  return (
    <span
      aria-hidden
      className={cn(
        "mr-0.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full transition-colors duration-200 ease-smooth-out",
        DOT_TOKEN_CLASS[kind],
      )}
    />
  );
}

/* ============================================================================
 * StatusChip
 * ========================================================================= */

export interface StatusChipProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children"> {
  /** Status (domain word or canonical state). Resolved via `STATUS_ALIAS`. */
  status: string;
  /** Override the auto-derived label (defaults to the canonical label). */
  label?: string;
  /** Chip size. */
  size?: "sm" | "default";
}

/**
 * StatusChip — a single lifecycle chip: leading dot + label, color from the
 * token map. `generating` shimmers its background and renders the label through
 * `ShinyText`; settled states are flat soft chips. The chip is a **stable node**
 * — changing `status` re-themes it in place (color tween + a transform-only
 * label flip), it never remounts, so a consumer can flip `draft → published` on
 * the same card without losing scroll or focus. The label sits in an
 * `aria-live="polite"` region so convergence is announced.
 */
export const StatusChip = React.forwardRef<HTMLSpanElement, StatusChipProps>(
  ({ status, label, size = "default", className, ...rest }, ref) => {
    const reduced = useReducedMotion() === true;
    const info = statusToken(status);
    const text = label ?? info.label;

    const labelNode = info.inProgress ? (
      // Solid, always-legible label. The in-progress *motion* is carried by the
      // chip's `animate-shimmer` background sweep below; the label itself stays a
      // flat `text-info` glyph. (We deliberately do NOT route it through
      // `ShinyText`: that clips a moving gradient to transparent-filled text, so
      // a static frame — screenshot, print, paused/exported card — renders the
      // label blank. The Brain's most common transient state must never read as
      // an empty pill. Verified by the #125 screenshot pass.)
      <span className="font-medium">{text}</span>
    ) : reduced ? (
      <span>{text}</span>
    ) : (
      // Transform-only flip on status change (keyed so the label re-mounts and
      // plays the micro scale-y); never opacity-from-0 so an export of a
      // mid-flip card is never blank. The outer chip node stays stable.
      <motion.span
        key={info.status}
        className="inline-block origin-left"
        initial={{ scaleY: 0.7 }}
        animate={{ scaleY: 1 }}
        transition={transitions.snappy}
      >
        {text}
      </motion.span>
    );

    return (
      <span
        ref={ref}
        data-status={info.status}
        aria-live="polite"
        className={cn(
          // Default (solid) appearance: each semantic variant already paints a
          // soft tinted chip (e.g. success = `bg-success/15 text-success`). The
          // `appearance="soft"` override is NOT used — it hardcodes `--info`
          // regardless of variant, which would tint every state blue.
          badgeVariants({ variant: info.badgeVariant, size }),
          "relative isolate overflow-hidden transition-colors duration-200 ease-smooth-out",
          className,
        )}
        {...rest}
      >
        {/* in-progress background shimmer (degrades to flat under reduced motion
            via the preset's global animate-shimmer guard) */}
        {info.inProgress ? (
          <span
            aria-hidden
            className="animate-shimmer pointer-events-none absolute inset-0 -z-10 rounded-full bg-info/10 bg-[linear-gradient(110deg,transparent,40%,hsl(var(--info)/0.18),60%,transparent)] bg-[length:200%_100%]"
          />
        ) : null}
        <StatusDot kind={info.dot} />
        {labelNode}
      </span>
    );
  },
);
StatusChip.displayName = "StatusChip";

/* ============================================================================
 * StatusStripe
 * ========================================================================= */

const STRIPE_TOKEN_CLASS: Record<StatusExpressionState, string> = {
  generating: "bg-info",
  ready: "bg-success",
  draft: "bg-muted-foreground/40",
  published: "bg-success",
  failed: "bg-destructive",
};

export interface StatusStripeProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Status (domain word or canonical state). */
  status: string;
  /** Render onto the consumer's own element (e.g. a card's `::before` slot). */
  asChild?: boolean;
}

/**
 * StatusStripe — a 2px accent stripe carrying the status color. The color tweens
 * on status change (`transition-colors`). Decorative (`aria-hidden`) — the
 * `StatusChip` is the accessible status.
 *
 * NOTE: for a *whole card*, prefer `statusRingClass()` — a complete keyline +
 * faint tint around the full perimeter. A single-edge stripe (e.g.
 * `absolute inset-y-0 left-0`) reads as an unintentional one-sided accent. This
 * primitive is kept for the narrow case of a small, deliberate peripheral edge
 * mark (e.g. a list row's leading rail), not as the card's status chrome.
 */
export const StatusStripe = React.forwardRef<HTMLSpanElement, StatusStripeProps>(
  ({ status, asChild = false, className, ...rest }, ref) => {
    const canonical = resolveStatus(status);
    const Comp = asChild ? Slot : "span";
    return (
      <Comp
        ref={ref}
        aria-hidden
        data-status={canonical}
        className={cn(
          "block w-0.5 rounded-full transition-colors duration-200 ease-smooth-out",
          STRIPE_TOKEN_CLASS[canonical],
          className,
        )}
        {...rest}
      />
    );
  },
);
StatusStripe.displayName = "StatusStripe";

/* ============================================================================
 * StatusProgress
 * ========================================================================= */

export interface StatusProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Status (domain word or canonical state). Renders only while generating. */
  status: string;
  /**
   * Determinate completion percent (0-100). Omit for the indeterminate sweep
   * (unknown completion). Provide when the caller has a real fraction, e.g.
   * file indexing N of M pages.
   */
  value?: number;
  /** Accessible description of a determinate value (e.g. "Indexing 3 of 7 pages"). */
  valueText?: string;
  /** Accessible name for the indeterminate sweep. Default "Generating". */
  label?: string;
}

/**
 * StatusProgress — a thin top-edge progress line, rendered **only** while the
 * status resolves to `generating` (hidden for every settled state). With a
 * `value` it is a determinate bar (`aria-valuenow`); without one it is an
 * indeterminate sweep (`role="progressbar"` with **no** `aria-valuenow`, per
 * the ARIA indeterminate contract). Under reduced motion the sweep holds a flat
 * tint instead of animating.
 */
export const StatusProgress = React.forwardRef<HTMLDivElement, StatusProgressProps>(
  ({ status, value, valueText, label = "Generating", className, ...rest }, ref) => {
    const reduced = useReducedMotion() === true;
    if (resolveStatus(status) !== "generating") return null;
    const determinate = typeof value === "number";
    const clamped = determinate ? Math.max(0, Math.min(100, value as number)) : 0;

    return (
      <div
        ref={ref}
        role="progressbar"
        aria-label={determinate ? undefined : label}
        aria-valuemin={determinate ? 0 : undefined}
        aria-valuemax={determinate ? 100 : undefined}
        aria-valuenow={determinate ? clamped : undefined}
        aria-valuetext={determinate ? (valueText ?? `${clamped}%`) : undefined}
        className={cn("h-0.5 w-full overflow-hidden rounded-full bg-info/15", className)}
        {...rest}
      >
        {determinate ? (
          <div
            className="h-full rounded-full bg-info transition-[width] duration-300 ease-smooth-out"
            style={{ width: `${clamped}%` }}
          />
        ) : reduced ? (
          // Indeterminate under reduced motion: hold a flat info fill (no stripe,
          // no motion) so the track still reads as "busy" without animating.
          <div className="h-full w-full rounded-full bg-info" />
        ) : (
          // Indeterminate: a seamless info stripe that walks by exactly one tile
          // (22px) forever. Because the gradient repeats and stays full-size, the
          // track is ALWAYS filled with moving stripes and never empties (the old
          // shimmer-slide bar slid off the container, leaving the track blank for
          // part of the cycle). The parent's overflow-hidden keeps it contained.
          <div
            className="animate-progress-stripe h-full w-full rounded-full"
            style={{
              backgroundImage:
                "repeating-linear-gradient(90deg, hsl(var(--info)) 0 10px, transparent 10px 22px)",
              backgroundSize: "22px 100%",
            }}
          />
        )}
      </div>
    );
  },
);
StatusProgress.displayName = "StatusProgress";
